import React, { useCallback, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import Modal from '../common/Modal';
import { fieldStyles } from '../../config/formStyles';
import { theme } from '../../config/theme';
import {
  formatCurrency,
  formatTasaForDisplay,
  formatComitenteForDisplay,
  formatCuitForDisplay,
  parseCuitDigits,
} from '../../utils/formatters';
import { claveDia } from '../../utils/caucionesImport';
import {
  conciliarEjecuciones,
  asignacionesDeImportadas,
  ESTADO_COMPLETA,
  ESTADO_PARCIAL,
  ESTADO_EXCEDIDA,
  MOTIVO_SIN_PEDIDO,
  MOTIVO_CARTERA_PROPIA,
  MOTIVO_SIN_COMITENTE,
} from '../../utils/caucionesConciliacion';
import { formatHoraForDisplay } from '../../utils/dateHelpers';
import {
  leerArchivoCauciones,
  fetchImportadas,
  guardarImportacion,
  crearCaucionDesdeSinImputar,
} from '../../services/caucionesImportService';
import { saveDatosFiscalesCliente } from '../../services/clientesService';

const PASO_ARCHIVO = 'archivo';
const PASO_CUITS = 'cuits';
const PASO_PREVIEW = 'preview';

const ETIQUETA_MOTIVO = {
  [MOTIVO_SIN_PEDIDO]: 'Sin pedido cargado en la planilla',
  [MOTIVO_CARTERA_PROPIA]: 'Cartera propia de Axon (la operación no trae CUIT de cliente)',
  [MOTIVO_SIN_COMITENTE]: 'CUIT sin comitente asociado',
};

const COLOR_ESTADO = {
  [ESTADO_COMPLETA]: { fondo: '#D1FAE5', texto: '#065F46' },
  [ESTADO_PARCIAL]: { fondo: '#FEF3C7', texto: '#92400E' },
  [ESTADO_EXCEDIDA]: { fondo: '#FEE2E2', texto: '#991B1B' },
};

const estilos = {
  dropZone: {
    border: `2px dashed ${theme.colors.border}`, borderRadius: theme.borderRadius,
    padding: '2rem', textAlign: 'center', backgroundColor: theme.colors.surface,
  },
  ayuda: {
    fontSize: '0.8rem', color: theme.colors.textSecondary, marginTop: '0.75rem', lineHeight: 1.5,
  },
  chips: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' },
  chip: (fondo, texto) => ({
    backgroundColor: fondo, color: texto, borderRadius: '999px',
    padding: '0.25rem 0.7rem', fontSize: '0.78rem', fontWeight: 600,
  }),
  seccion: {
    fontFamily: theme.typography.titleFamily, fontSize: '0.8rem', fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: '0.05em', color: theme.colors.primary,
    borderBottom: `1px solid ${theme.colors.border}`, paddingBottom: '0.35rem',
    margin: '1.25rem 0 0.5rem 0',
  },
  tabla: { width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' },
  th: {
    textAlign: 'left', padding: '0.4rem 0.5rem', color: theme.colors.textSecondary,
    fontWeight: 600, borderBottom: `1px solid ${theme.colors.border}`, whiteSpace: 'nowrap',
  },
  td: { padding: '0.4rem 0.5rem', borderBottom: `1px solid ${theme.colors.border}`, verticalAlign: 'middle' },
  tdNum: { padding: '0.4rem 0.5rem', borderBottom: `1px solid ${theme.colors.border}`, textAlign: 'right', whiteSpace: 'nowrap' },
  badge: (estado) => ({
    backgroundColor: (COLOR_ESTADO[estado] || {}).fondo || theme.colors.border,
    color: (COLOR_ESTADO[estado] || {}).texto || theme.colors.secondary,
    borderRadius: '4px', padding: '0.15rem 0.45rem', fontSize: '0.72rem', fontWeight: 600,
  }),
  aviso: {
    backgroundColor: '#FEF3C7', border: '1px solid #FDE68A', color: '#92400E',
    padding: '0.6rem 0.8rem', borderRadius: theme.borderRadius, fontSize: '0.8rem', marginBottom: '0.75rem',
  },
  botonChico: {
    ...fieldStyles.button, padding: '0.3rem 0.7rem', fontSize: '0.75rem',
  },
  vacio: { fontSize: '0.8rem', color: theme.colors.textSecondary, margin: '0.35rem 0' },
};

/**
 * Importa el archivo de operaciones del mercado y lo concilia contra las
 * cauciones ya cargadas en la planilla.
 *
 * Tres pasos: elegir archivo -> resolver los CUIT que no se pueden mapear a un
 * comitente (sólo A3, que identifica al cliente por CUIT) -> revisar y confirmar.
 *
 * Nada se escribe hasta que el trader confirma en el último paso.
 */
const ImportarCaucionesModal = ({
  isOpen,
  onClose,
  onImportado,
  cauciones = [],
  clientesDb = {},
  comitentePorCuit = {},
  userName,
  userId,
}) => {
  const inputRef = useRef(null);
  const [paso, setPaso] = useState(PASO_ARCHIVO);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [lectura, setLectura] = useState(null);
  const [cuitsResueltos, setCuitsResueltos] = useState({});
  const [asignaciones, setAsignaciones] = useState({});
  const [creando, setCreando] = useState({});
  const [comitentesElegidos, setComitentesElegidos] = useState({});

  const opcionesComitente = useMemo(
    () =>
      Object.entries(clientesDb)
        .map(([nro, nombre]) => ({ value: nro, label: `${nombre} - ${formatComitenteForDisplay(nro)}` }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [clientesDb]
  );

  const reiniciar = useCallback(() => {
    setPaso(PASO_ARCHIVO);
    setLectura(null);
    setCuitsResueltos({});
    setAsignaciones({});
    setCreando({});
    setComitentesElegidos({});
    setError('');
    setCargando(false);
  }, []);

  const cerrar = () => {
    reiniciar();
    onClose();
  };

  // Índice CUIT -> comitente: el padrón más lo que el trader resuelve a mano.
  const mapaCuits = useMemo(
    () => ({ ...comitentePorCuit, ...cuitsResueltos }),
    [comitentePorCuit, cuitsResueltos]
  );

  /** Completa el nroComitente de las partidas de A3 usando el CUIT. */
  const partidasResueltas = useMemo(() => {
    if (!lectura) return [];
    return lectura.partidas.map((partida) => {
      if (partida.nroComitente || !partida.cuit) return partida;
      return { ...partida, nroComitente: mapaCuits[parseCuitDigits(partida.cuit)] || '' };
    });
  }, [lectura, mapaCuits]);

  const cuitsSinResolver = useMemo(() => {
    const pendientes = new Map();
    partidasResueltas.forEach((partida) => {
      if (partida.nroComitente || !partida.cuit) return;
      const cuit = parseCuitDigits(partida.cuit);
      if (!pendientes.has(cuit)) pendientes.set(cuit, { cuit, capital: 0, partidas: 0, moneda: partida.moneda });
      const acumulado = pendientes.get(cuit);
      acumulado.capital += Number(partida.capital) || 0;
      acumulado.partidas += 1;
    });
    return [...pendientes.values()].sort((a, b) => b.capital - a.capital);
  }, [partidasResueltas]);

  const conciliacion = useMemo(
    () => conciliarEjecuciones({ partidas: partidasResueltas, cauciones, asignaciones }),
    [partidasResueltas, cauciones, asignaciones]
  );

  const handleArchivo = async (evento) => {
    const file = evento.target.files?.[0];
    evento.target.value = '';
    if (!file) return;

    setCargando(true);
    setError('');
    try {
      const resultado = await leerArchivoCauciones(file);
      if (resultado.error) {
        setError(resultado.error);
        setCargando(false);
        return;
      }
      if (resultado.partidas.length === 0) {
        const sinEjecutar = resultado.ignoradas.filter((i) => i.sinEjecutar).length;
        setError(
          sinEjecutar > 0
            ? `Se leyó "${file.name}" pero ninguna orden de caución está ejecutada todavía ` +
                `(${sinEjecutar} sin ejecutar). Volvé a subirlo cuando se ejecuten.`
            : `Se leyó "${file.name}" (${resultado.mercado}) pero no quedó ninguna caución para importar. ` +
                `Se descartaron ${resultado.ignoradas.length} fila(s); revisá que el archivo sea de la rueda de hoy.`
        );
        setLectura(resultado);
        setCargando(false);
        return;
      }
      // Lo que ya se importó antes queda en la caución a la que se imputó: si
      // no se lee, resubir el archivo le borraría los parciales.
      const previas = await fetchImportadas();
      setLectura(resultado);
      setCuitsResueltos({});
      setAsignaciones(asignacionesDeImportadas(resultado.partidas, previas, cauciones));
      setCargando(false);
      // El paso de CUIT sólo aplica a A3, que identifica al cliente por CUIT.
      const faltanCuits = resultado.partidas.some(
        (p) => !p.nroComitente && p.cuit && !comitentePorCuit[parseCuitDigits(p.cuit)]
      );
      setPaso(faltanCuits ? PASO_CUITS : PASO_PREVIEW);
    } catch (e) {
      setError(e.message);
      setCargando(false);
    }
  };

  const elegirComitenteParaCuit = (cuit, nroComitente) => {
    setCuitsResueltos((previo) => {
      const siguiente = { ...previo };
      if (nroComitente) siguiente[cuit] = nroComitente;
      else delete siguiente[cuit];
      return siguiente;
    });
  };

  /** Persiste los CUIT resueltos en el padrón para no volver a preguntarlos. */
  const confirmarCuits = async () => {
    setCargando(true);
    setError('');
    try {
      await Promise.all(
        Object.entries(cuitsResueltos).map(([cuit, nroComitente]) =>
          saveDatosFiscalesCliente(nroComitente, {
            cuit: formatCuitForDisplay(cuit),
            tipoDocumento: 'CUIT',
          })
        )
      );
      setPaso(PASO_PREVIEW);
    } catch (e) {
      // Que falle el guardado del padrón no debería frenar la importación.
      console.error('ImportarCaucionesModal.confirmarCuits failed:', e);
      setError('No se pudieron guardar todos los CUIT en el padrón de clientes, pero podés seguir con la importación.');
      setPaso(PASO_PREVIEW);
    } finally {
      setCargando(false);
    }
  };

  const reasignarGrupo = (partidas, caucionId) => {
    setAsignaciones((previo) => {
      const siguiente = { ...previo };
      partidas.forEach((p) => {
        siguiente[p.idExterno] = caucionId;
      });
      return siguiente;
    });
  };

  const descartarPartida = (idExterno) => {
    setAsignaciones((previo) => ({ ...previo, [idExterno]: '' }));
  };

  /**
   * Lo excedido no se guarda sin que el trader lo confirme: una caución que
   * recibe más de lo pedido suele ser una orden que ya estaba cargada en otra
   * caución, y guardarla así la cuenta dos veces en el saldo del cliente.
   */
  const confirmaExcedidas = () => {
    const excedidas = conciliacion.porCaucion.filter((g) => g.estadoConciliacion === ESTADO_EXCEDIDA);
    if (excedidas.length === 0) return true;
    const detalle = excedidas
      .map(
        (g) =>
          `• ${g.caucion.cliente || formatComitenteForDisplay(g.caucion.nroComitente)}: pedido ` +
          `${formatCurrency(g.montoPedido, g.caucion.moneda)}, operado ${formatCurrency(g.capitalOperado, g.caucion.moneda)}`
      )
      .join('\n');
    return window.confirm(
      `${excedidas.length} caución(es) reciben más de lo pedido:\n\n${detalle}\n\n` +
        'Si alguna de esas órdenes ya estaba cargada en otra caución, se va a contar dos veces en el saldo. ' +
        'Podés descartarla en la tabla antes de guardar.\n\n¿Imputar igual?'
    );
  };

  const confirmar = async () => {
    if (!confirmaExcedidas()) return;
    setCargando(true);
    setError('');
    try {
      const resumen = await guardarImportacion({
        conciliacion,
        archivoNombre: lectura.archivoNombre,
        userName,
        userId,
      });
      if (onImportado) onImportado({ ...resumen, mercado: lectura.mercado });
      cerrar();
    } catch (e) {
      setError(`No se pudo guardar la importación: ${e.message}`);
      setCargando(false);
    }
  };

  const crearDesdeGrupo = async (grupo) => {
    const nroComitente = grupo.nroComitente || comitentesElegidos[grupo.clave];
    if (!nroComitente) {
      setError('Elegí el comitente antes de crear la caución.');
      return;
    }
    setCreando((previo) => ({ ...previo, [grupo.clave]: true }));
    setError('');
    try {
      await crearCaucionDesdeSinImputar(grupo, {
        nroComitente,
        cliente: clientesDb[nroComitente] || '',
        userName,
        userId,
        archivoNombre: lectura.archivoNombre,
      });
      // Se saca del preview: ya quedó creada y con sus parciales.
      setLectura((previo) => ({
        ...previo,
        partidas: previo.partidas.filter(
          (p) => !grupo.partidas.some((usada) => usada.idExterno === p.idExterno)
        ),
      }));
    } catch (e) {
      setError(`No se pudo crear la caución: ${e.message}`);
    } finally {
      setCreando((previo) => ({ ...previo, [grupo.clave]: false }));
    }
  };

  const { resumen } = conciliacion;
  const ordenesSinEjecutar = lectura ? lectura.ignoradas.filter((i) => i.sinEjecutar).length : 0;

  const footer = (() => {
    if (paso === PASO_ARCHIVO) {
      return (
        <button type="button" style={{ ...fieldStyles.button, ...fieldStyles.secondaryButton }} onClick={cerrar}>
          Cerrar
        </button>
      );
    }
    if (paso === PASO_CUITS) {
      const faltan = cuitsSinResolver.length;
      return (
        <>
          <button type="button" style={{ ...fieldStyles.button, ...fieldStyles.secondaryButton }} onClick={cerrar}>
            Cancelar
          </button>
          <button type="button" style={fieldStyles.button} onClick={confirmarCuits} disabled={cargando}>
            {faltan > 0 ? `Seguir sin resolver ${faltan}` : 'Continuar'}
          </button>
        </>
      );
    }
    const sinImputarTotal = resumen.partidasLeidas - resumen.partidasImputadas;
    const etiqueta = resumen.partidasImputadas > 0
      ? `Imputar ${resumen.partidasImputadas} partida(s)`
      // Aun sin nada que imputar conviene guardar: así las operaciones sin
      // pedido quedan registradas en el panel de conciliación.
      : `Registrar ${sinImputarTotal} partida(s) sin imputar`;
    return (
      <>
        <button type="button" style={{ ...fieldStyles.button, ...fieldStyles.secondaryButton }} onClick={cerrar}>
          Cancelar
        </button>
        <button
          type="button"
          style={fieldStyles.button}
          onClick={confirmar}
          disabled={cargando || resumen.partidasLeidas === 0}
        >
          {cargando ? 'Guardando…' : etiqueta}
        </button>
      </>
    );
  })();

  const titulo =
    paso === PASO_CUITS
      ? 'Importar operaciones — asociar CUIT a comitente'
      : paso === PASO_PREVIEW
        ? `Importar operaciones — revisar (${lectura?.mercado || ''})`
        : 'Importar operaciones de cauciones';

  return (
    <Modal isOpen={isOpen} onClose={cerrar} title={titulo} maxWidth="1000px" error={error} footer={footer}>
      {paso === PASO_ARCHIVO && (
        <div>
          <div style={estilos.dropZone}>
            <p style={{ margin: '0 0 1rem 0', fontWeight: 600, color: theme.colors.primary }}>
              Subí el archivo de operaciones que bajás del sistema con el que operás
            </p>
            <input
              ref={inputRef}
              type="file"
              accept=".csv,.xls,.xlsx"
              onChange={handleArchivo}
              style={{ display: 'none' }}
            />
            <button
              type="button"
              style={fieldStyles.button}
              onClick={() => inputRef.current?.click()}
              disabled={cargando}
            >
              {cargando ? 'Leyendo…' : 'Elegir archivo'}
            </button>
            <p style={estilos.ayuda}>
              Es el <strong>listado de órdenes</strong> (Captura de Órdenes); se siguen aceptando los export
              anteriores de BYMA y de A3. Podés subirlo todas las veces que quieras durante la rueda: lo ya
              importado no se duplica y las órdenes que se terminaron de ejecutar se actualizan.
            </p>
          </div>
          {lectura && lectura.ignoradas.length > 0 && (
            <>
              <div style={estilos.seccion}>Filas descartadas ({lectura.ignoradas.length})</div>
              <ul style={{ fontSize: '0.78rem', color: theme.colors.textSecondary, paddingLeft: '1.2rem' }}>
                {lectura.ignoradas.slice(0, 15).map((ignorada, i) => (
                  <li key={`${ignorada.fila}-${i}`}>Fila {ignorada.fila}: {ignorada.motivo}</li>
                ))}
                {lectura.ignoradas.length > 15 && <li>… y {lectura.ignoradas.length - 15} más</li>}
              </ul>
            </>
          )}
        </div>
      )}

      {paso === PASO_CUITS && (
        <div>
          <div style={estilos.aviso}>
            El archivo de A3 identifica al cliente por CUIT. Estos no están asociados a ningún comitente todavía:
            elegilo una vez y queda guardado en el padrón de clientes para las próximas importaciones.
          </div>
          <table style={estilos.tabla}>
            <thead>
              <tr>
                <th style={estilos.th}>CUIT</th>
                <th style={estilos.th}>Partidas</th>
                <th style={estilos.th}>Capital</th>
                <th style={estilos.th}>Comitente</th>
              </tr>
            </thead>
            <tbody>
              {cuitsSinResolver.map((pendiente) => (
                <tr key={pendiente.cuit}>
                  <td style={estilos.td}>{formatCuitForDisplay(pendiente.cuit)}</td>
                  <td style={estilos.tdNum}>{pendiente.partidas}</td>
                  <td style={estilos.tdNum}>{formatCurrency(pendiente.capital, pendiente.moneda)}</td>
                  <td style={{ ...estilos.td, minWidth: '320px' }}>
                    <Select
                      options={opcionesComitente}
                      value={
                        opcionesComitente.find((o) => o.value === cuitsResueltos[pendiente.cuit]) || null
                      }
                      onChange={(opcion) => elegirComitenteParaCuit(pendiente.cuit, opcion?.value || '')}
                      placeholder="Buscar comitente…"
                      isClearable
                      menuPortalTarget={document.body}
                      styles={fieldStyles.reactSelect}
                    />
                  </td>
                </tr>
              ))}
              {cuitsSinResolver.length === 0 && (
                <tr>
                  <td style={estilos.td} colSpan={4}>Todos los CUIT quedaron asociados.</td>
                </tr>
              )}
            </tbody>
          </table>
          <p style={estilos.ayuda}>
            Si dejás alguno sin asociar, sus operaciones se listan aparte y no se imputan a ninguna caución.
          </p>
        </div>
      )}

      {paso === PASO_PREVIEW && (
        <div>
          <div style={estilos.chips}>
            <span style={estilos.chip('#E0E7FF', '#3730A3')}>
              {lectura.mercado} · rueda del {claveDia(lectura.fechaRueda)}
            </span>
            <span style={estilos.chip('#D1FAE5', '#065F46')}>{resumen.completas} completas</span>
            <span style={estilos.chip('#FEF3C7', '#92400E')}>{resumen.parciales} parciales</span>
            <span style={estilos.chip('#FEE2E2', '#991B1B')}>{resumen.excedidas} excedidas</span>
            <span style={estilos.chip(theme.colors.border, theme.colors.secondary)}>
              {resumen.noOperadas} sin operar
            </span>
            {resumen.yaCargadas > 0 && (
              <span style={estilos.chip('#E0F2FE', '#075985')}>{resumen.yaCargadas} ya cargadas a mano</span>
            )}
            {resumen.sinPedido + resumen.sinComitente + resumen.carteraPropia > 0 && (
              <span style={estilos.chip('#FDE68A', '#78350F')}>
                {resumen.sinPedido + resumen.sinComitente + resumen.carteraPropia} sin imputar
              </span>
            )}
          </div>

          {ordenesSinEjecutar > 0 && (
            <div style={estilos.aviso}>
              {ordenesSinEjecutar} orden(es) de caución todavía no se ejecutaron y no se imputan. Cuando se
              ejecuten, volvé a subir el archivo: lo ya importado no se duplica.
            </div>
          )}

          {lectura.advertencias.length > 0 && (
            <div style={estilos.aviso}>
              <strong>Revisar:</strong> en {lectura.advertencias.length} operación(es) el monto de vencimiento no
              cierra con capital, tasa y plazo. Se importan igual, pero conviene mirarlas.
              <ul style={{ margin: '0.4rem 0 0 0', paddingLeft: '1.2rem' }}>
                {lectura.advertencias.slice(0, 5).map((a, i) => (
                  <li key={`${a.idExterno}-${i}`}>{a.motivo}</li>
                ))}
              </ul>
            </div>
          )}

          <div style={estilos.seccion}>Se van a imputar ({conciliacion.porCaucion.length} cauciones)</div>
          {conciliacion.porCaucion.length === 0 ? (
            <p style={estilos.vacio}>Ninguna operación del archivo matcheó con una caución cargada.</p>
          ) : (
            <table style={estilos.tabla}>
              <thead>
                <tr>
                  <th style={estilos.th}>Cliente</th>
                  <th style={estilos.th}>Op.</th>
                  <th style={estilos.th}>Moneda</th>
                  <th style={{ ...estilos.th, textAlign: 'right' }}>Pedido</th>
                  <th style={{ ...estilos.th, textAlign: 'right' }}>Operado</th>
                  <th style={{ ...estilos.th, textAlign: 'right' }}>Dif.</th>
                  <th style={{ ...estilos.th, textAlign: 'right' }}>Tasa</th>
                  <th style={{ ...estilos.th, textAlign: 'right' }}>Part.</th>
                  <th style={estilos.th}>Resultado</th>
                </tr>
              </thead>
              <tbody>
                {conciliacion.porCaucion.map((grupo) => (
                  <tr key={grupo.caucion.id}>
                    <td style={estilos.td}>
                      {grupo.caucion.cliente || formatComitenteForDisplay(grupo.caucion.nroComitente)}
                      {grupo.variosCandidatos && (
                        <div style={{ marginTop: '0.3rem' }}>
                          <select
                            style={{ ...fieldStyles.select, fontSize: '0.72rem', padding: '0.2rem' }}
                            value={grupo.caucion.id}
                            onChange={(e) => reasignarGrupo(grupo.partidas, e.target.value)}
                          >
                            {/* La caución actual va siempre: si la partida ya estaba imputada a
                                una caución Hecha, no figura entre las candidatas abiertas. */}
                            {[
                              grupo.caucion,
                              ...(grupo.partidas[0]?.candidatos || []).filter((c) => c.id !== grupo.caucion.id),
                            ].map((candidata) => (
                              <option key={candidata.id} value={candidata.id}>
                                Pedido de {formatCurrency(candidata.monto || 0, candidata.moneda)} · {candidata.comercial || 's/comercial'}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      {grupo.mercadoDistinto && (
                        <div style={{ fontSize: '0.7rem', color: '#92400E' }}>
                          El pedido era para {grupo.caucion.mercado}, se operó en {grupo.mercados.join(', ')}
                        </div>
                      )}
                      {/* Excedida casi siempre es una orden de más: se muestran para poder sacarla. */}
                      {grupo.estadoConciliacion === ESTADO_EXCEDIDA && grupo.partidas.length > 1 && (
                        <ul style={{ margin: '0.3rem 0 0 0', paddingLeft: '1rem', fontSize: '0.72rem' }}>
                          {grupo.partidas.map((p) => (
                            <li key={p.idExterno} style={{ marginBottom: '0.2rem' }}>
                              {p.mercado} · {formatCurrency(p.capital, p.moneda)} · {formatTasaForDisplay(p.tasa)}{' '}
                              <button
                                type="button"
                                style={{ ...estilos.botonChico, ...fieldStyles.secondaryButton, padding: '0.1rem 0.4rem' }}
                                onClick={() => descartarPartida(p.idExterno)}
                              >
                                Descartar
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td style={estilos.td}>{grupo.caucion.operacion}</td>
                    <td style={estilos.td}>{grupo.caucion.moneda}</td>
                    <td style={estilos.tdNum}>
                      {grupo.montoPedido > 0 ? formatCurrency(grupo.montoPedido, grupo.caucion.moneda) : 'a definir'}
                    </td>
                    <td style={estilos.tdNum}>{formatCurrency(grupo.capitalOperado, grupo.caucion.moneda)}</td>
                    <td style={{ ...estilos.tdNum, color: grupo.diferencia === 0 ? 'inherit' : '#92400E' }}>
                      {grupo.montoPedido > 0 ? formatCurrency(grupo.diferencia, grupo.caucion.moneda) : '—'}
                    </td>
                    <td style={estilos.tdNum}>{formatTasaForDisplay(grupo.tasaPonderada)}</td>
                    <td style={estilos.tdNum}>{grupo.partidas.length}</td>
                    <td style={estilos.td}>
                      <span style={estilos.badge(grupo.estadoConciliacion)}>{grupo.estadoConciliacion}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {conciliacion.yaCargadas.length > 0 && (
            <>
              <div style={estilos.seccion}>Ya cargadas a mano ({conciliacion.yaCargadas.length})</div>
              <table style={estilos.tabla}>
                <thead>
                  <tr>
                    <th style={estilos.th}>Cliente</th>
                    <th style={estilos.th}>Op.</th>
                    <th style={estilos.th}>Moneda</th>
                    <th style={{ ...estilos.th, textAlign: 'right' }}>Capital</th>
                    <th style={{ ...estilos.th, textAlign: 'right' }}>Tasa</th>
                    <th style={estilos.th}>Caución cerrada a mano</th>
                    <th style={estilos.th} />
                  </tr>
                </thead>
                <tbody>
                  {conciliacion.yaCargadas.map((p) => (
                    <tr key={p.idExterno}>
                      <td style={estilos.td}>{p.caucion.cliente || formatComitenteForDisplay(p.nroComitente)}</td>
                      <td style={estilos.td}>{p.operacion}</td>
                      <td style={estilos.td}>{p.moneda}</td>
                      <td style={estilos.tdNum}>{formatCurrency(p.capital, p.moneda)}</td>
                      <td style={estilos.tdNum}>{formatTasaForDisplay(p.tasa)}</td>
                      <td style={{ ...estilos.td, fontSize: '0.75rem' }}>
                        {p.caucion.mercado} · cargada {formatHoraForDisplay(p.caucion.createdAt)} · operó{' '}
                        {p.caucion.operador || '—'}
                      </td>
                      <td style={estilos.td}>
                        {p.candidatos.length > 0 && (
                          <button
                            type="button"
                            style={estilos.botonChico}
                            onClick={() => reasignarGrupo([p], p.candidatos[0].id)}
                          >
                            Imputar al pedido abierto
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p style={estilos.ayuda}>
                Estas órdenes coinciden en cliente, moneda, dirección y monto con una caución que ya se cerró a mano:
                no se imputan de nuevo para no contarlas dos veces. Si es otra operación, imputala al pedido abierto.
              </p>
            </>
          )}

          {conciliacion.sinImputar.length > 0 && (
            <>
              <div style={estilos.seccion}>Operado sin pedido cargado ({conciliacion.sinImputar.length})</div>
              <table style={estilos.tabla}>
                <thead>
                  <tr>
                    <th style={estilos.th}>Comitente / CUIT</th>
                    <th style={estilos.th}>Op.</th>
                    <th style={estilos.th}>Moneda</th>
                    <th style={{ ...estilos.th, textAlign: 'right' }}>Capital</th>
                    <th style={{ ...estilos.th, textAlign: 'right' }}>Tasa</th>
                    <th style={estilos.th}>Motivo</th>
                    <th style={estilos.th} />
                  </tr>
                </thead>
                <tbody>
                  {conciliacion.sinImputar.map((grupo) => (
                    <tr key={grupo.clave}>
                      <td style={{ ...estilos.td, minWidth: '260px' }}>
                        {grupo.nroComitente ? (
                          `${clientesDb[grupo.nroComitente] || ''} ${formatComitenteForDisplay(grupo.nroComitente)}`
                        ) : (
                          <>
                            <div style={{ marginBottom: '0.3rem' }}>
                              {grupo.cuit ? formatCuitForDisplay(grupo.cuit) : 'sin identificar'}
                            </div>
                            <Select
                              options={opcionesComitente}
                              value={opcionesComitente.find((o) => o.value === comitentesElegidos[grupo.clave]) || null}
                              onChange={(opcion) =>
                                setComitentesElegidos((previo) => ({ ...previo, [grupo.clave]: opcion?.value || '' }))
                              }
                              placeholder="Asignar comitente…"
                              isClearable
                              menuPortalTarget={document.body}
                              styles={fieldStyles.reactSelect}
                            />
                          </>
                        )}
                      </td>
                      <td style={estilos.td}>{grupo.operacion}</td>
                      <td style={estilos.td}>{grupo.moneda}</td>
                      <td style={estilos.tdNum}>{formatCurrency(grupo.capitalTotal, grupo.moneda)}</td>
                      <td style={estilos.tdNum}>{formatTasaForDisplay(grupo.tasaPonderada)}</td>
                      <td style={{ ...estilos.td, fontSize: '0.75rem' }}>{ETIQUETA_MOTIVO[grupo.motivo] || grupo.motivo}</td>
                      <td style={estilos.td}>
                        <button
                          type="button"
                          style={estilos.botonChico}
                          onClick={() => crearDesdeGrupo(grupo)}
                          disabled={
                            creando[grupo.clave] || (!grupo.nroComitente && !comitentesElegidos[grupo.clave])
                          }
                        >
                          {creando[grupo.clave] ? 'Creando…' : 'Crear caución'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p style={estilos.ayuda}>
                Crear la caución la deja directamente en estado Hecha con los datos del archivo. Lo que no crees
                queda registrado como pendiente de conciliar, sin imputarse a ninguna caución.
              </p>
            </>
          )}

          {conciliacion.noOperadas.length > 0 && (
            <>
              <div style={estilos.seccion}>Quedan sin operar ({conciliacion.noOperadas.length})</div>
              <table style={estilos.tabla}>
                <thead>
                  <tr>
                    <th style={estilos.th}>Cliente</th>
                    <th style={estilos.th}>Op.</th>
                    <th style={estilos.th}>Moneda</th>
                    <th style={{ ...estilos.th, textAlign: 'right' }}>Pedido</th>
                    <th style={estilos.th}>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {conciliacion.noOperadas.map((c) => (
                    <tr key={c.id}>
                      <td style={estilos.td}>{c.cliente || formatComitenteForDisplay(c.nroComitente)}</td>
                      <td style={estilos.td}>{c.operacion}</td>
                      <td style={estilos.td}>{c.moneda}</td>
                      <td style={estilos.tdNum}>
                        {Number(c.monto) > 0 ? formatCurrency(c.monto, c.moneda) : 'a definir'}
                      </td>
                      <td style={estilos.td}>{c.estado}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {lectura.ignoradas.length > 0 && (
            <p style={estilos.ayuda}>
              Se descartaron {lectura.ignoradas.length} fila(s) del archivo (operaciones que no son cauciones,
              órdenes sin ejecutar y, en el archivo de A3, espejos de mercado y patas de otras ruedas).
            </p>
          )}
        </div>
      )}
    </Modal>
  );
};

export default ImportarCaucionesModal;
